/**
 * Client-side mirror of the gateway's modelFallbacks validation (TRA-923),
 * plus the duplicate rule the gateway does not enforce yet, plus the
 * same-upstream rule (TRA-1383): a fallback served by the primary's own
 * upstream is the same machine as the primary, so it is no backup at all.
 * Pure module so the editor component file stays fast-refreshable.
 */

import type { FallbackOptionsResponse } from '@/api/generated/model'

/**
 * The upstream a route value is served by: a catalog slug resolves to its
 * upstream, a known upstream id is itself, anything else is null.
 */
function upstreamOf(value: string, options: FallbackOptionsResponse): string | null {
  const catalog = options.catalog.find((c) => c.slug === value)
  if (catalog) return catalog.upstream_id
  return options.upstreams.some((u) => u.id === value) ? value : null
}

/**
 * True when the value is served by the primary's upstream. The gateway
 * compares the raw strings, so `baseten/glm-5.2` and bare `baseten` look
 * different to it; here they resolve to the same upstream. An unknown
 * primary resolves to nothing and never matches.
 */
function sharesUpstreamWithPrimary(
  value: string,
  effectivePrimary: string,
  options: FallbackOptionsResponse,
): boolean {
  const primaryUpstream = upstreamOf(effectivePrimary, options)
  return primaryUpstream !== null && upstreamOf(value, options) === primaryUpstream
}

export interface DroppedPrimary {
  chain: string[]
  /** What was taken out, in chain order. */
  removed: string[]
}

/**
 * The chain without every entry the new primary makes useless: the primary
 * value itself, and anything served by the primary's upstream.
 */
export function dropPrimaryFromChain(
  chain: string[],
  effectivePrimary: string,
  options: FallbackOptionsResponse,
): DroppedPrimary {
  const clashes = (value: string): boolean =>
    value === effectivePrimary || sharesUpstreamWithPrimary(value, effectivePrimary, options)
  return {
    chain: chain.filter((value) => !clashes(value)),
    removed: chain.filter(clashes),
  }
}

/**
 * Every message names the offending value. An empty chain is valid — it
 * means "remove the chain", never an empty array on the wire (the gateway
 * rejects those as a no-op that reads like protection).
 */
export function validateFallbackChain(
  chain: string[],
  options: FallbackOptionsResponse,
  effectivePrimary: string,
): string[] {
  const known = new Set([
    ...options.catalog.map((c) => c.slug),
    ...options.upstreams.map((u) => u.id),
  ])
  const deployable = new Set(
    options.upstreams.filter((u) => u.has_deployment).map((u) => u.id),
  )
  const errors: string[] = []
  const seen = new Set<string>()
  for (const value of chain) {
    if (value === effectivePrimary) {
      errors.push(
        `'${value}' is the model's primary route. A fallback must point somewhere else.`,
      )
    } else if (sharesUpstreamWithPrimary(value, effectivePrimary, options)) {
      errors.push(
        `'${value}' is served by the same upstream as the primary, so it cannot be a fallback.`,
      )
    }
    if (!known.has(value)) {
      errors.push(
        `'${value}' is not in the public catalog and is not an existing private upstream.`,
      )
    } else if (!value.includes('/') && !deployable.has(value)) {
      // No self-serve flow creates this slot today (repointing MOVES the
      // one slot rather than adding a second) — it has to be registered on
      // the upstream out-of-band, so say that instead of prescribing an
      // impossible in-app fix.
      errors.push(
        `Upstream '${value}' has no deployment for this model, so the fallback would 404 exactly when the primary is down. A deployment keyed by this model's slug must be registered on that upstream (gateway admin) before it can serve as a fallback.`,
      )
    }
    if (seen.has(value)) {
      errors.push(`'${value}' appears more than once in the chain.`)
    }
    seen.add(value)
  }
  // A value present three times emits its messages twice — byte-identical
  // strings the editor keys elements by, so return each message once.
  return [...new Set(errors)]
}
